import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { offersAPI, alertsAPI, peekCache } from '../services/api';
import SourceCard, { SourceGrid } from '../components/SourceCard';
import { ORDINARY_AVAILABILITY } from '../styles/layout';
import {
    PageHeader, SearchField, Switch, Pager, Alert, Icon, SourceDot,
    sourceColor, brandMark, fmtNum, fmtMoney, fmtPct, timeAgo, syncServerClock, seasonLabel,
} from '../ui';
import './DashboardPage.css';

const PAGE_SIZE = 25;
const EMPTY_FILTERS = { source: '', search: '', season: '', width: '', profil: '', diametr: '' };
const FEED_HOURS = 72;

const today = () => {
    const s = new Date().toLocaleDateString('uk-UA', { weekday: 'long', day: 'numeric', month: 'long' });
    return s.charAt(0).toUpperCase() + s.slice(1);
};

// Що саме сталося з ціною конкурента по нашому товару (див. price_monitor._classify).
const ALERT_KINDS = {
    undercut: { label: 'дешевше за нас', chip: 'bad' },
    gone: { label: 'дешевший зник', chip: 'good' },
    drop: { label: 'знизив ціну', chip: 'down' },
    rise: { label: 'підняв ціну', chip: 'up' },
};

const DashboardPage = () => {
    const { user, isApproved } = useAuth();
    const approved = isApproved();

    // Останні відомі дані з кешу — показуємо одразу, свіжі приходять у фоні.
    const [summary, setSummary] = useState(() => peekCache('/offers/dashboard', { recent_hours: 24 }) || null);
    const [facets, setFacets] = useState(() => peekCache('/offers/facets', { source: '' }) || null);
    const [feed, setFeed] = useState(() => peekCache('/alerts', { hours: FEED_HOURS, limit: 8 }) || null);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // За замовчуванням показуємо лише позиції з живою ціною: у infoshina 87%
    // каталогу без наявності, і без цього фільтра перші сторінки — суцільне
    // «немає в наявності», що робить пошук марним.
    const [withPriceOnly, setWithPriceOnly] = useState(true);
    const [page, setPage] = useState(0);
    const [data, setData] = useState(() =>
        peekCache('/offers', { limit: PAGE_SIZE, offset: 0, has_price: true }) || { total: 0, offers: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Дані конкурентів доступні лише схваленим користувачам — інакше бекенд
    // поверне 403, а користувач побачить порожню сторінку з помилкою.
    useEffect(() => {
        if (!approved) return;
        (async () => {
            try {
                const [s, f] = await Promise.all([offersAPI.dashboard(), offersAPI.facets()]);
                syncServerClock(s.now);
                setSummary(s);
                setFacets(f);
            } catch (err) {
                console.error('Dashboard load failed:', err);
                setError('Не вдалося завантажити зведення по джерелах.');
            }
        })();
        alertsAPI.list({ hours: FEED_HOURS, limit: 8 })
            .then(setFeed)
            .catch(err => { console.error('Alerts load failed:', err); setFeed({ alerts: [], total: 0 }); });
    }, [approved]);

    const loadOffers = useCallback(async () => {
        if (!approved) return;
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            if (withPriceOnly) params.has_price = true;
            setData(await offersAPI.list(params));
        } catch (err) {
            console.error('Offers load failed:', err);
            setData({ total: 0, offers: [] });
        } finally {
            setLoading(false);
        }
    }, [approved, filters, withPriceOnly, page]);

    // Затримка — лише поки користувач друкує в пошуку; відкриття сторінки
    // і зміна фільтрів запитують одразу.
    const lastSearch = useRef(filters.search);
    useEffect(() => {
        const typing = lastSearch.current !== filters.search;
        lastSearch.current = filters.search;
        const t = setTimeout(loadOffers, typing ? 300 : 0);
        return () => clearTimeout(t);
    }, [loadOffers, filters.search]);

    const setFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(0);
    };

    const sources = summary?.sources || [];
    const activeFilterCount = Object.entries(filters).filter(([k, v]) => k !== 'search' && v).length;
    const sourceIndex = (key) => Math.max(0, sources.findIndex(s => s.source_key === key));
    const sourceTitle = (key) => sources.find(s => s.source_key === key)?.title || key;
    const totalChanges = sources.reduce((n, s) => n + (s.recent_changes || 0), 0);
    const totalOffers = sources.reduce((n, s) => n + (s.total || 0), 0);
    const lastFetch = sources.map(s => s.last_updated).filter(Boolean).sort().pop();

    const filterSelect = (key, label, options, render = v => v) => (
        <select
            value={filters[key]}
            onChange={e => setFilter(key, e.target.value)}
            className={`ui-select-pill${filters[key] ? ' is-on' : ''}`}
            aria-label={label}
        >
            <option value="">{label}</option>
            {options.map(v => <option key={v} value={v}>{render(v)}</option>)}
        </select>
    );

    return (
        <div className="ui-page">
            <PageHeader
                eyebrow={today()}
                title="Огляд ринку"
                actions={approved && lastFetch && (
                    <span className="dash-live">
                        <span className="ui-live-dot" />
                        Останній збір {timeAgo(lastFetch)}
                    </span>
                )}
            />

            {user?.status === 'pending' && (
                <Alert kind="warn">
                    <b>Обліковий запис чекає на схвалення.</b> Доступ до даних з'явиться,
                    щойно адміністратор його підтвердить.
                </Alert>
            )}
            {user?.status === 'rejected' && (
                <Alert kind="error">
                    <b>Доступ відхилено.</b> Зверніться до адміністратора.
                </Alert>
            )}
            {error && <Alert kind="error">{error}</Alert>}

            {approved && (
                <>
                    {/* ── Головні цифри ── */}
                    <section className="dash-hero">
                        <div className="dash-hero-accent">
                            <div className="dash-hero-top">
                                <span>Змін цін за {summary?.recent_hours ?? 24} год</span>
                                {sources.length > 0 && (
                                    <span className="dash-hero-pill">
                                        {sources.length} {sources.length === 1 ? 'конкурент' : sources.length < 5 ? 'конкуренти' : 'конкурентів'}
                                    </span>
                                )}
                            </div>
                            <div>
                                <div className="dash-hero-num num">{summary ? fmtNum(totalChanges) : '…'}</div>
                                <div className="dash-hero-sub">з {fmtNum(totalOffers)} пропозицій у базі</div>
                            </div>
                        </div>

                        <div className="ui-card dash-split">
                            <div className="ui-card-head" style={{ marginBottom: 0 }}>
                                <h3>Хто змінював ціни</h3>
                                <Link to="/price-dynamics" className="dash-link">Динаміка цін <Icon name="arrowRight" size={16} /></Link>
                            </div>
                            <div className="dash-bar" role="img"
                                 aria-label={sources.map(s => `${s.title}: ${s.recent_changes}`).join(', ')}>
                                {totalChanges > 0
                                    ? sources.filter(s => s.recent_changes > 0).map(s => (
                                        <div key={s.source_key}
                                             style={{ flex: `${s.recent_changes} 1 0`, background: sourceColor(s.source_key, sourceIndex(s.source_key)) }} />
                                    ))
                                    : <div style={{ flex: 1, background: 'var(--surface-3)' }} />}
                            </div>
                            <div className="dash-legend">
                                {sources.map((s, i) => (
                                    <div key={s.source_key}>
                                        <SourceDot sourceKey={s.source_key} index={i} />
                                        <span className="muted-2">{s.title}</span>
                                        <b className="num">{fmtNum(s.recent_changes)}</b>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* ── Джерела ── */}
                    <section className="dash-section">
                        <div className="ui-card-head" style={{ marginBottom: 0 }}>
                            <h2 className="ui-section-title">Конкуренти</h2>
                            <Link to="/competitors" className="dash-link">Усі джерела <Icon name="arrowRight" size={16} /></Link>
                        </div>
                        <SourceGrid>
                            {sources.map((s, i) => <SourceCard key={s.source_key} source={s} index={i} />)}
                        </SourceGrid>
                    </section>

                    <div className="dash-columns">
                        {/* ── Наскрізний пошук по всіх джерелах ── */}
                        <section className="ui-card ui-card--flush">
                            <div className="dash-search">
                                <div className="ui-card-head" style={{ marginBottom: 0 }}>
                                    <h2 className="ui-section-title">Пошук по всіх конкурентах</h2>
                                    {activeFilterCount > 0 && (
                                        <button className="ui-btn ui-btn--sm" onClick={() => { setFilters(f => ({ ...EMPTY_FILTERS, search: f.search })); setPage(0); }}>
                                            Скинути фільтри · {activeFilterCount}
                                        </button>
                                    )}
                                </div>
                                <SearchField large value={filters.search} onChange={v => setFilter('search', v)}
                                             placeholder="Назва, бренд або розмір, напр. 205/55 R16" />
                                <div className="ui-toolbar">
                                    {filterSelect('source', 'Усі джерела', sources.map(s => s.source_key), sourceTitle)}
                                    {filterSelect('season', 'Сезон', facets?.seasons || [], seasonLabel)}
                                    {filterSelect('width', 'Ширина', facets?.widths || [])}
                                    {filterSelect('profil', 'Профіль', facets?.profils || [])}
                                    {filterSelect('diametr', 'Діаметр', facets?.diametrs || [])}
                                    <span style={{ marginLeft: 'auto' }}>
                                        <Switch checked={withPriceOnly} onChange={v => { setWithPriceOnly(v); setPage(0); }}>
                                            Тільки з ціною
                                        </Switch>
                                    </span>
                                </div>
                                <div className="muted" style={{ fontSize: 13 }}>
                                    Знайдено <b className="num" style={{ color: 'var(--text)' }}>{fmtNum(data.total)}</b>
                                    {withPriceOnly && ' · позиції без наявності приховані'}
                                </div>
                            </div>

                            <div className="dash-list" style={{ opacity: loading ? 0.5 : 1 }}>
                                {data.offers.length === 0 ? (
                                    <div className="muted" style={{ padding: '24px 12px' }}>{loading ? 'Завантаження…' : 'Нічого не знайдено.'}</div>
                                ) : data.offers.map(o => {
                                    const Row = o.url ? 'a' : 'div';
                                    return (
                                        <Row key={o.id} className="dash-row"
                                             {...(o.url ? { href: o.url, target: '_blank', rel: 'noreferrer' } : {})}>
                                            <span className="ui-mark">{brandMark(o.brand)}</span>
                                            <span className="dash-row-main">
                                                <span className="ellipsis dash-row-name">{o.name}</span>
                                                <span className="dash-row-sub">
                                                    <span>{o.brand || '—'}</span>
                                                    <span className="ui-chip num" style={{ height: 22 }}>{o.width}/{o.profil} {o.diametr}</span>
                                                    {o.season && <span>{seasonLabel(o.season)}</span>}
                                                </span>
                                            </span>
                                            <span className="dash-row-source">
                                                <SourceDot sourceKey={o.source_key} index={sourceIndex(o.source_key)} />
                                                {sourceTitle(o.source_key)}
                                            </span>
                                            <span className="dash-row-price">
                                                {o.price != null ? (
                                                    <>
                                                        <b className="num">{fmtMoney(o.price)}</b>
                                                        {o.availability && o.availability !== ORDINARY_AVAILABILITY && (
                                                            <span className="dash-row-avail">{o.availability}</span>
                                                        )}
                                                    </>
                                                ) : <span className="muted" style={{ fontSize: 13 }}>немає в наявності</span>}
                                            </span>
                                        </Row>
                                    );
                                })}
                            </div>
                            <Pager page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} />
                        </section>

                        {/* ── Значимі зміни по наших товарах ── */}
                        <section className="ui-card dash-feed">
                            <div className="ui-card-head" style={{ marginBottom: 4 }}>
                                <h3>Що змінилось по наших товарах</h3>
                            </div>
                            <p className="muted" style={{ fontSize: 13, margin: '0 0 8px' }}>
                                Конкурент став дешевшим за нас, дешевший зник або ціна змінилась на 3% і більше —
                                за останні {FEED_HOURS / 24} дні.
                            </p>
                            {feed == null ? (
                                <div className="muted" style={{ padding: '16px 0' }}>Завантаження…</div>
                            ) : feed.alerts.length === 0 ? (
                                <div className="muted" style={{ padding: '16px 0' }}>Значимих змін не було.</div>
                            ) : feed.alerts.map(a => {
                                const kind = ALERT_KINDS[a.kind] || { label: a.kind, chip: 'accent' };
                                const Row = a.competitor_url ? 'a' : 'div';
                                return (
                                    <Row key={a.id} className="dash-feed-row"
                                         {...(a.competitor_url ? { href: a.competitor_url, target: '_blank', rel: 'noreferrer' } : {})}>
                                        <span className="dash-feed-main">
                                            <span className="ellipsis dash-feed-name" title={a.name}>{a.name || `Товар ${a.my_id}`}</span>
                                            <span className="dash-feed-sub">
                                                <SourceDot sourceKey={a.source_key} index={sourceIndex(a.source_key)} />
                                                {sourceTitle(a.source_key)} · {timeAgo(a.created_at)}
                                            </span>
                                        </span>
                                        <span className="dash-feed-right">
                                            <b className="num">{a.new_price != null ? fmtMoney(a.new_price) : 'немає'}</b>
                                            <span className={`ui-chip ui-chip--${kind.chip}`}>
                                                {a.change_pct != null && a.kind !== 'undercut' ? fmtPct(a.change_pct) : kind.label}
                                            </span>
                                        </span>
                                    </Row>
                                );
                            })}
                            {feed?.total > (feed?.alerts?.length || 0) && (
                                <div className="muted" style={{ fontSize: 13, paddingTop: 12 }}>
                                    І ще {fmtNum(feed.total - feed.alerts.length)} за цей період.
                                </div>
                            )}
                        </section>
                    </div>
                </>
            )}
        </div>
    );
};

export default DashboardPage;
